import Api from '@vacationtracker/shared/services/api'
import { IEnergyLevelApiResponse, EnergyMode, EnergyCheckInType } from '@vacationtracker/shared/types/user-insight'
import { EnergyLevelData, PTOSuggestionsResponse } from '../types'
import { transformEnergyScoreResponse } from './transformer'

export type FormatMessageFunction = (descriptor: { id: string }, values?: Record<string, any>) => string

// Simple cache to prevent duplicate API calls
const apiCache = new Map<string, Promise<any>>()

export const getPTOSuggestions = async (fromDate: string, toDate: string, limit = 3): Promise<PTOSuggestionsResponse> => {
  const url = `/insights/pto-suggestions?fromDate=${fromDate}&toDate=${toDate}&limit=${limit}`
  const response = await Api.get(url)
  return response
}

export const getEnergyLevelData = async (referenceTimestamp?: string, formatMessage?: FormatMessageFunction): Promise<EnergyLevelData> => {
  // Create cache key based on timestamp
  const cacheKey = referenceTimestamp || 'default'

  // Return cached promise if it exists
  if (apiCache.has(cacheKey)) {
    return apiCache.get(cacheKey)
  }

  // Create new API call promise
  const apiPromise = (async () => {
    try {
      let url = '/insights/energy-level'
      if (referenceTimestamp) {
        url += `?referenceTimestamp=${encodeURIComponent(referenceTimestamp)}`
      }

      const response = await Api.get<IEnergyLevelApiResponse>(url)

      // Check if we have valid data
      if (!response.latestEnergyScore?.meta) {
        throw new Error('NO_DATA_AVAILABLE')
      }

      if (!response.score && (!response.history || response.history.length === 0)) {
        throw new Error('NO_DATA_AVAILABLE')
      }

      return transformEnergyScoreResponse(response, formatMessage)
    } catch (error) {
      // Only log actual network/API errors, not our validation errors
      if (error instanceof Error && error.message !== 'NO_DATA_AVAILABLE') {
        console.error('Error fetching energy level data:', error)
      }
      throw error
    }
  })()

  // Cache the promise
  apiCache.set(cacheKey, apiPromise)

  // Clean up cache after a delay to prevent memory leaks
  setTimeout(() => {
    apiCache.delete(cacheKey)
  }, 5000) // 5 seconds

  return apiPromise
}

export const sendEnergyScoreFeedback = async (
  score: number,
  additionalData?: {
    feedback?: string
    allowContact?: boolean
    originalScore?: number
    userPerception?: number
  }
): Promise<void> => {
  try {
    await Api.post('/insights/feedback', {
      type: 'FEEDBACK',
      feedback: {
        score: score, // User's perceived score
        ...(additionalData?.originalScore !== undefined && { originalScore: additionalData.originalScore }),
        ...(additionalData?.feedback && { userFeedback: additionalData.feedback }),
        ...(additionalData?.allowContact !== undefined && { allowContact: additionalData.allowContact }),
      },
    })
  } catch (error) {
    console.error('Error sending feedback:', error)
    throw error
  }
}

export const submitEnergyCheckIn = async (type: EnergyCheckInType, energyMode: EnergyMode): Promise<void> => {
  try {
    await Api.post('/insights/energy-checkin', {
      type,
      energyMode,
    })
  } catch (error) {
    console.error('Error submitting energy checkin:', error)
    throw error
  }
}
